import { formatDateTime } from '../../lib/format';

/** The first part of an action ("charger.update" → "charger"), for the What filter. */
export const AREA_LABELS: Record<string, string> = {
  card: 'RFID cards',
  charger: 'Chargers',
  driver: 'Drivers',
  onboardingLink: 'Onboarding links',
  organisation: 'Organisations',
  payToCharge: 'Public charging',
  powersensor: 'Powersensor',
  rate_schedule: 'Peak and off-peak hours',
  session: 'Sessions',
  site: 'Sites',
  tariff: 'Prices',
  user: 'People and sign-ins',
};

const ACTIONS: Record<string, string> = {
  'card.create': 'Added an RFID card',
  'card.update': 'Changed an RFID card',
  'card.delete': 'Deleted an RFID card',
  'charger.accept': 'Accepted a charger',
  'charger.create': 'Added a charger',
  'charger.reject': 'Rejected a charger',
  'charger.update': 'Changed charger settings',
  'charger.security': 'Changed charger security',
  'charger.remove': 'Removed a charger',
  'charger.erase': 'Erased a charger',
  'driver.create': 'Added a driver',
  'driver.update': 'Changed a driver',
  'driver.approve': 'Approved a driver',
  'driver.reject': 'Rejected a driver sign-up',
  'driver.deactivate': 'Deactivated a driver',
  'driver.billing': 'Changed driver billing',
  'driver.signUp': 'A driver signed up',
  'onboardingLink.create': 'Made an onboarding link',
  'onboardingLink.revoke': 'Turned off an onboarding link',
  'organisation.create': 'Added an organisation',
  'organisation.update': 'Changed an organisation',
  'organisation.deactivate': 'Removed an organisation',
  'payToCharge.refund': 'Refunded a guest',
  'powersensor.fetch': 'Downloaded Powersensor readings',
  'rate_schedule.create': 'Added peak and off-peak hours',
  'rate_schedule.update': 'Changed peak and off-peak hours',
  'rate_schedule.delete': 'Deleted peak and off-peak hours',
  'user.mfa.setup': 'Set up two-factor sign-in',
  'user.mfa.failed': 'Wrong sign-in code',
  'user.mfa.reset': 'Reset two-factor sign-in',
  'user.mfa.off': 'Turned off two-factor sign-in',
  'user.mfa.on': 'Turned on two-factor sign-in',
  'user.mfa.forgetDevices': 'Forgot remembered devices',
  'session.reuse': 'Reused a session',
  'site.create': 'Added a site',
  'site.update': 'Changed a site',
  'site.deactivate': 'Removed a site',
  'tariff.create': 'Set a price',
  'tariff.cancel': 'Cancelled a price change',
  'user.create': 'Added a person',
  'user.update': 'Changed a person',
  'user.deactivate': 'Deactivated a person',
  'user.login': 'Signed in',
  'user.login.failed': 'Failed sign-in',
  'user.logout': 'Signed out',
  'user.passwordChange': 'Changed their password',
  'user.passwordReset': 'Reset their password',
  'user.passwordReset.requested': 'Asked for a password reset',
  'user.passwordReset.sent': 'Password reset email sent',
  'user.welcome.sent': 'Welcome email sent again',
  'apikey.create': 'API key created',
  'apikey.revoke': 'API key revoked',
  'user.temporaryPassword': 'Set a temporary password',
  'user.unlock': 'Unlocked a person',
};

/** "charger.update" → "Changed charger settings"; "charger.command.Reset" → "Charger command: Reset". */
export function actionLabel(action: string): string {
  if (ACTIONS[action]) return ACTIONS[action];
  const cmd = /^charger\.command\.(\w+)$/.exec(action);
  if (cmd) return `Charger command: ${cmd[1]!.replace(/([a-z])([A-Z])/g, '$1 $2')}`;
  const [area, ...rest] = action.split('.');
  return `${AREA_LABELS[area!] ?? area}: ${rest.join(' ')}`;
}

/** "pricePerKwh" → "Price per kwh"; a few spelled out. */
const FIELDS: Record<string, string> = {
  pricePerKwh: 'Price per kWh',
  idleFeePerMinute: 'Idle fee per minute',
  infinityevRatePerKwh: 'Infinity EV rate per kWh',
  infinityevIdleFeePerMinute: 'Infinity EV idle share',
  ratePerKwh: 'Own rate',
  powersensorEnabled: 'Powersensor',
  powersensorRate: 'Powersensor rate',
  rateScheduleId: 'Peak and off-peak hours',
  kind: 'Price',
  periods: 'Periods',
  ratedKw: 'Rated kW',
  authMethod: 'Sessions start by',
  publicCharging: 'Public charging',
  chargePointId: 'Charge point ID',
  evSensorNumber: 'Powersensor appliance',
};
export function fieldLabel(key: string): string {
  if (FIELDS[key]) return FIELDS[key];
  const words = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Readable value: dates, yes/no, lists; "—" for empty. */
export function formatValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) return formatDateTime(v);
  if (Array.isArray(v)) {
    if (!v.length) return 'None';
    if (v.every((x) => typeof x !== 'object')) return v.join(', ');
    // A list of things (e.g. cards): one line each, by its name and tag.
    return v.map((x) => itemLine(x)).join('\n');
  }
  if (typeof v === 'object') {
    return Object.entries(v as Record<string, unknown>)
      .filter(([k, x]) => x !== null && x !== undefined && !HIDDEN_KEYS.test(k))
      .map(([k, x]) => `${fieldLabel(k)}: ${typeof x === 'object' ? JSON.stringify(x) : formatValue(x)}`)
      .join('\n');
  }
  return String(v);
}

/** IDs and bookkeeping that mean nothing to a reader. */
const HIDDEN_KEYS = /^(id|.*Id|createdAt|updatedAt|timezone)$/;

/** "Tony_Smappee_Card · 04298DAA461190 · off" */
function itemLine(x: unknown): string {
  if (!x || typeof x !== 'object') return formatValue(x);
  const o = x as Record<string, unknown>;
  const name = o.label ?? o.name ?? o.businessName ?? o.displayName ?? o.title;
  const parts = [name, o.tag ?? o.code ?? o.email, o.isActive === false ? 'off' : null, o.isExpired === true ? 'expired' : null].filter((p) => p !== null && p !== undefined && p !== '');
  return parts.length ? parts.map(String).join(' · ') : JSON.stringify(o);
}

export interface Change {
  field: string;
  before: unknown;
  after: unknown;
}

/** Bookkeeping fields that change on every save and say nothing. */
const SKIP = new Set(['updatedAt', 'activeSessions', 'connectorStatus', 'lastSeenAt', 'isConnected', 'lastBootAt']);

/** Field by field, what's different between before and after (or everything, for something new or removed). */
export function changesOf(before: unknown, after: unknown): Change[] {
  const obj = (v: unknown) => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null);
  const b = obj(before);
  const a = obj(after);
  if (!b && !a) return [];
  const keys = [...new Set([...Object.keys(b ?? {}), ...Object.keys(a ?? {})])].filter((k) => !SKIP.has(k));
  const out: Change[] = [];
  for (const k of keys) {
    const x = b?.[k];
    const y = a?.[k];
    if (b && a && JSON.stringify(x ?? null) === JSON.stringify(y ?? null)) continue;
    out.push({ field: k, before: x, after: y });
  }
  return out;
}
